import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { formatCompactNumber } from "@/lib/format-compact-number"
import type { TikTokProfile } from "@/types/submissions"

/**
 * OKS-391 — TikTok profile-page-style identity strip rendered above the
 * picker grid (UX Spec §2.2). Left: circular avatar (initials fallback);
 * right: `@handle` (bold) + a 3-column stat row (Following / Followers /
 * Likes), each value over its label.
 *
 * Read-only — the strip is NOT a link out to TikTok (PRD OQ-5). Stat
 * values come from the latest captured metrics snapshot and collapse to
 * "—" when absent (`formatCompactNumber` handles the null case).
 *
 * Rendered only when `profile` is non-null; the caller (`TikTokPostPicker`)
 * hides the strip entirely on identity/auth errors.
 */
interface Props {
  collabId: number
  profile: TikTokProfile
}

export function TikTokProfileStrip({ collabId, profile }: Props) {
  const { t } = useTranslation()

  const stats: { key: string; label: string; value: number | null }[] = [
    {
      key: "following",
      label: t("submissions.picker.stat_following"),
      value: profile.following_count,
    },
    {
      key: "followers",
      label: t("submissions.picker.stat_followers"),
      value: profile.follower_count,
    },
    {
      key: "likes",
      label: t("submissions.picker.stat_likes"),
      value: profile.likes_count,
    },
  ]

  return (
    <div
      data-testid={`tiktok-picker-profile-${collabId}`}
      className="flex items-center gap-3 rounded-lg border p-3"
    >
      <Avatar className="size-12 shrink-0">
        {profile.avatar_url && <AvatarImage src={profile.avatar_url} alt="" />}
        <AvatarFallback>{initials(profile)}</AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <p
          className="truncate font-semibold"
          aria-label={t("submissions.picker.profile_aria", {
            handle: profile.handle,
          })}
        >
          @{profile.handle}
        </p>
        <div className="mt-1 grid grid-cols-3 gap-2 text-center">
          {stats.map((stat) => (
            <div key={stat.key} className="min-w-0">
              <span className="block truncate text-sm font-semibold tabular-nums">
                {formatCompactNumber(stat.value)}
              </span>
              <span className="text-muted-foreground block truncate text-xs">
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** First 1-2 letters of display name (preferred) or handle, upper-cased. */
function initials(profile: TikTokProfile): string {
  const name = profile.display_name?.trim()
  // Truthy (not nullish) fallback: an empty/whitespace display_name must
  // fall through to the handle, so `??` would be wrong here.
  const source = name && name.length > 0 ? name : profile.handle
  return source.slice(0, 2).toUpperCase()
}
